import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Box, Container, IconButton, Step, StepButton, StepLabel, Stepper } from "@mui/material";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { BrandEnum } from "../_common/enum/brand.enum";
import { FormTemplateTypeEnum } from "../_common/enum/forms/formTemplateType.enum";
import { FormTemplateSectionVisibilityEnum } from "../_common/enum/forms/formTemplateSectionVisibility.enum";
import { FormFieldKeyConditionEnum } from "../_common/enum/forms/formFieldKeyCondition.enum";
import { FormFieldKeyConditionTypeEnum } from "../_common/enum/forms/formFieldKeyConditionType.enum";
import { IFormProgress } from "../_common/interface/form/iFormProgress.interface";
import { IFormManage } from "../_common/models/form/formManage.model";
import { IFormSectionConditionDependency, IFormSectionList } from "../_common/models/formSection";
import { FormProgressChips } from "../_components/form/formProgresChips";
import { toastr } from "../_components/toastr/toastr";
import { getBrandHQCompletedLink } from "../_helpers/brand.helper";
import { getStringDateNormalized } from "../_helpers/dateTime.helper";
import { handleErrorResponse } from "../_helpers/handleResponse.helper";
import { retryHeight, sendContainerHeightToParent, sendScrollMessageToParent } from "../_helpers/iFrameSize.helper";
import { getSectionApproxMinutes } from "../_helpers/formProgress.helper";
import { formService } from "../_services/form.service";
import { formSectionsMoreThanFiveStyles } from "../_styles/formSection.styles";
import { FormSectionOriginalManage } from "./formSection/formSectionOriginal-manage";

export function FormOriginalManage(params: { brand: BrandEnum }) {
    const navigate = useNavigate();

    const { formId, quoteId } = useParams<{ formId?: string; quoteId?: string }>();
    const [searchParams] = useSearchParams();

    const token = searchParams.get("token");

    const _formId = Number(formId);
    const _quoteId = !!quoteId ? Number(quoteId) : null;

    const [data, setData] = useState<IFormManage>();
    const [isLoading, setLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const [activeStep, setActiveStep] = useState<number | null>(null);
    const [stepWithLatestId, setStepWithLatestId] = useState<number | null>(null);
    const [progress, setProgress] = useState<IFormProgress | null>(null);

    const hasSentScrollRef = useRef(false);
    const prevStepRef = useRef<number | null>(null);

    const stepperScrollRef = useRef<HTMLDivElement | null>(null);
    const stepRefs = useRef<Array<HTMLDivElement | null>>([]);

    const effectiveFormId = token ? data?.id ?? _formId : _formId;

    const sectionCount = data?.formSectionList?.length ?? 0;
    const showProgressBar =
        (data?.FormTemplateTypeId === FormTemplateTypeEnum.HealthQuestionnaireProvider ||
            data?.formTemplateTypeId === FormTemplateTypeEnum.FactFind) ||
        sectionCount > 5;
    const completedSections = data?.formSectionList?.filter((s) => !!s.id).length ?? 0;

    useEffect(() => {
        setActiveStep(0);
    }, []);

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [_formId, _quoteId]);

    useLayoutEffect(() => {
        if (!data || activeStep === null) return;

        requestAnimationFrame(() => {
            retryHeight(sendContainerHeightToParent);
        });
    }, [data, activeStep]);

    useEffect(() => {
        if (!data) return;
        if (hasSentScrollRef.current) return;

        hasSentScrollRef.current = true;

        const id = setTimeout(() => {
            try {
                sendScrollMessageToParent();
            } catch {
            }
        }, 400);

        return () => clearTimeout(id);
    }, [data]);

    useEffect(() => {
        if (activeStep === null) return;

        const prev = prevStepRef.current;
        prevStepRef.current = activeStep;

        if (prev === null) return;
        if (prev === activeStep) return;

        const id = setTimeout(() => {
            try {
                window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
                sendScrollMessageToParent();
            } catch (e) {
                console.warn("[FORM ORIGINAL] scroll error", e);
            }
        }, 300);

        return () => clearTimeout(id);
    }, [activeStep]);

    useEffect(() => {
        if (sectionCount <= 5) return;
        if (activeStep === null || !data?.formSectionList) return;

        const fv = buildFieldValueMap(data.fromSectionsIdsList);
        const displayIdx = fullStepToDisplayStep(activeStep, data.formSectionList, fv);

        requestAnimationFrame(() => {
            const el = stepRefs.current[displayIdx];
            if (!el) return;
            el.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
        });
    }, [activeStep, sectionCount, data?.formSectionList, data?.fromSectionsIdsList]);

    useEffect(() => {
        if (!effectiveFormId) {
            setProgress(null);
            return;
        }

        fetchProgress();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [effectiveFormId]);

    function fetchProgress() {
        if (!effectiveFormId) return;

        formService
            .getProgress(effectiveFormId)
            .then((json: IFormProgress) => setProgress(json))
            .catch(() => {
                setProgress(null);
            });
    }

    function fetchData(isOnSave?: boolean) {
        setLoading(true);

        const request = token ? formService.getByToken(token) : formService.get(_formId, _quoteId);

        return request
            .then((json: IFormManage) => {
                setData(json);
                return json;
            })
            .finally(() => {
                setLoading(false);
                if (isOnSave === true) setIsSaving(false);
            });
    }

    function buildFieldValueMap(fromSectionsIdsList?: IFormSectionList[]): Map<number, string> {
        const map = new Map<number, string>();
        fromSectionsIdsList?.forEach((section) => {
            section.formFields?.forEach((ff) => {
                if (ff.formFieldKeyId) map.set(ff.formFieldKeyId, ff.fieldValue ?? "");
            });
        });
        return map;
    }

    function getSavedFieldValues(): Map<number, string> {
        return buildFieldValueMap(data?.fromSectionsIdsList);
    }

    function checkSectionCondition(dep: IFormSectionConditionDependency, fieldValue: string | undefined): boolean {
        const val = fieldValue ?? "";
        switch (dep.formFieldKeyConditionId) {
            case FormFieldKeyConditionEnum.EqualsTo:
                return val.toLowerCase() === (dep.value ?? "").toLowerCase();
            case FormFieldKeyConditionEnum.EqualsOrGreater:
                return Number(val || 0) >= Number(dep.value || 0);
            case FormFieldKeyConditionEnum.EqualsOrLess:
                return Number(val || 0) <= Number(dep.value || 0);
            case FormFieldKeyConditionEnum.Greater:
                return Number(val || 0) > Number(dep.value || 0);
            case FormFieldKeyConditionEnum.Less:
                return Number(val || 0) < Number(dep.value || 0);
            case FormFieldKeyConditionEnum.Contains:
                return !!val && !!dep.value && val.toLowerCase().includes(dep.value.toLowerCase());
            case FormFieldKeyConditionEnum.ContainsNot:
                return !!val && !!dep.value && !val.toLowerCase().includes(dep.value.toLowerCase());
            default:
                return false;
        }
    }

    function isSectionEnabledWith(section: IFormSectionList, fieldValues: Map<number, string>): boolean {
        if (!section.hasFormFieldConditionDependency) return true;
        const deps = section.formTemplateSectionConditionDependencies;
        if (!deps || deps.length === 0) return true;

        const isAny = section.formFieldKeyConditionTypeId === FormFieldKeyConditionTypeEnum.Any;
        let result: boolean | null = null;

        for (const dep of deps) {
            const conditionMet = checkSectionCondition(dep, fieldValues.get(dep.conditionFormFieldKeyId));
            if (result === null) {
                result = conditionMet;
            } else if (isAny) {
                result = result || conditionMet;
                if (result) break;
            } else {
                result = result && conditionMet;
                if (!result) break;
            }
        }

        return result === true;
    }

    function isSectionEnabled(section: IFormSectionList): boolean {
        return isSectionEnabledWith(section, getSavedFieldValues());
    }

    function isSectionShownInStepper(section: IFormSectionList, fv: Map<number, string>): boolean {
        const hasConditions =
            !!section.hasFormFieldConditionDependency &&
            (section.formTemplateSectionConditionDependencies?.length ?? 0) > 0;
        if (!hasConditions) return true;
        if (isSectionEnabledWith(section, fv)) return true;
        const vis = section.formTemplateSectionVisibilityId ?? FormTemplateSectionVisibilityEnum.Disabled;
        return vis !== FormTemplateSectionVisibilityEnum.Invisible;
    }

    function getVisibleSectionIndices(list: IFormSectionList[], fv: Map<number, string>): number[] {
        return list.map((_, i) => i).filter((i) => isSectionShownInStepper(list[i], fv));
    }

    function getNextAllowedIncompleteFullIndex(
        list: IFormSectionList[],
        stepWithLatestId: number | null,
        fv: Map<number, string>
    ): number | null {
        if (stepWithLatestId === null) return null;
        for (let i = stepWithLatestId + 1; i < list.length; i++) {
            if (!isSectionShownInStepper(list[i], fv)) continue;
            if (!list[i].id) return i;
        }
        return null;
    }

    function fullStepToDisplayStep(fullStep: number | null, list: IFormSectionList[], fv: Map<number, string>): number {
        const vis = getVisibleSectionIndices(list, fv);
        if (fullStep === null) return 0;
        if (fullStep >= list.length) return Math.max(0, vis.length - 1);
        const idx = vis.indexOf(fullStep);
        if (idx >= 0) return idx;
        const fallback = vis.find((i) => i >= fullStep);
        return fallback !== undefined ? vis.indexOf(fallback) : Math.max(0, vis.length - 1);
    }

    function completeForm() {
        if (!effectiveFormId) {
            console.error("No effectiveFormId, cannot complete form");
            return;
        }

        setIsSaving(true);

        return formService
            .complete(effectiveFormId)
            .then(
                () => {
                    toastr.success("success");

                    const brand = params.brand;

                    if (!brand) {
                        console.error("No brand provided");
                        return;
                    }

                    const url = getBrandHQCompletedLink(brand);

                    try {
                        if (window.top && window.top !== window.self) {
                            window.top.location.href = url;
                        } else {
                            window.location.href = url;
                        }
                    } catch (e) {
                        console.warn("Top navigation blocked, falling back to iframe navigation", e);
                        window.location.href = url;
                    }
                },
                (err) => {
                    handleErrorResponse(err);
                }
            )
            .finally(() => {
                setIsSaving(false);
            });
    }

    function onSave() {
        if (data && activeStep !== null && activeStep === data.formSectionList.length - 1) {
            completeForm();
        } else {
            triggerNextStep(true);
        }
    }

    function triggerNextStep(isOnSave?: boolean) {
        if (!data || !data.formSectionList?.length) return;

        const isForward = activeStep === getActiveStep();
        const currentStep = activeStep ?? 0;
        const maxIndex = data.formSectionList.length - 1;

        fetchData(isOnSave)
            ?.then((freshData) => {
                if (!freshData) return;

                if (isForward) {
                    const intendedNext = currentStep + 1;
                    const fieldValues = buildFieldValueMap(freshData.fromSectionsIdsList);

                    let targetStep = intendedNext;
                    while (targetStep < freshData.formSectionList.length && !isSectionEnabledWith(freshData.formSectionList[targetStep], fieldValues)) {
                        targetStep++;
                    }

                    const boundedStep = Math.min(targetStep, maxIndex);
                    setActiveStep(boundedStep);
                    setStepWithLatestId(boundedStep - 1);
                } else {
                    const fv = buildFieldValueMap(freshData.fromSectionsIdsList);
                    let step = freshData.formSectionList.findIndex(
                        (item) => !item.id && isSectionShownInStepper(item, fv)
                    );
                    if (step < 0) {
                        step = freshData.formSectionList.findIndex((item) => !item.id);
                    }
                    setActiveStep(step >= 0 ? step : 0);
                }
            })
            .finally(() => {
                fetchProgress();
            });
    }

    function getActiveStep() {
        if (!data?.formSectionList?.length) return 0;

        const fv = buildFieldValueMap(data.fromSectionsIdsList);
        let step: number | null = null;
        data.formSectionList.forEach((item, index) => {
            if (!item.id && step === null && isSectionShownInStepper(item, fv)) step = index;
        });
        if (step !== null) return step;
        data.formSectionList.forEach((item, index) => {
            if (!item.id && step === null) step = index;
        });

        return step ?? 0;
    }

    function scrollStepper(direction: "left" | "right") {
        if (!stepperScrollRef.current) return;

        const container = stepperScrollRef.current;
        const amount = Math.max(240, Math.floor(container.clientWidth * 0.7));

        container.scrollBy({ left: direction === "left" ? -amount : amount, behavior: "smooth" });
    }

    function renderSectionStepLabel(listItem: IFormSectionList) {
        return <StepLabel>{listItem.name}</StepLabel>;
    }

    const stepperFieldValues = useMemo(
        () => buildFieldValueMap(data?.fromSectionsIdsList),
        [data?.fromSectionsIdsList]
    );

    const visibleSectionIndices = useMemo(
        () => (data?.formSectionList ? getVisibleSectionIndices(data.formSectionList, stepperFieldValues) : []),
        [data?.formSectionList, stepperFieldValues]
    );

    const displayStep = useMemo(() => {
        if (!data?.formSectionList || activeStep === null) return 0;
        return fullStepToDisplayStep(activeStep, data.formSectionList, stepperFieldValues);
    }, [data?.formSectionList, activeStep, stepperFieldValues]);

    const nextSectionEstimate = useMemo(() => {
        if (!data?.formSectionList?.length) return null;

        const nextFullIndex =
            activeStep !== null && activeStep < data.formSectionList.length ? activeStep : getActiveStep();

        if (nextFullIndex === null || nextFullIndex >= data.formSectionList.length) return null;

        const section = data.formSectionList[nextFullIndex];
        if (!section) return null;

        const approxMinutes = getSectionApproxMinutes(progress, section.formTemplateSectionId);

        return {
            name: section.name,
            approxMinutes
        };
    }, [activeStep, data?.formSectionList, progress]);

    const lastVisibleFullIndex =
        visibleSectionIndices.length > 0 ? visibleSectionIndices[visibleSectionIndices.length - 1] : null;

    const isLastSection =
        !!data &&
        activeStep !== null &&
        lastVisibleFullIndex !== null &&
        activeStep === lastVisibleFullIndex;

    return (
        <>
            <Container id="formContainer">
                {activeStep !== null && data && data.formSectionList.length > 0 && (
                    <>
                        <h2 style={{ textAlign: "center", marginBottom: "8px" }}>{data.displayName}</h2>

                        <div style={{ width: "100%", textAlign: "center", marginBottom: "16px" }}>
                            <i>Your progress to this point has been saved. You may access this form at any time before submission.</i>
                        </div>

                        {data.dateCompleted && <h6 style={{ width: "100%", textAlign: "center" }}>Completed Date: {getStringDateNormalized(data.dateCompleted, true)}</h6>}

                        {showProgressBar && (
                            <FormProgressChips
                                completedSections={progress?.completedSections!}
                                totalSections={progress?.totalSections!}
                                completedFields={progress?.completedFields}
                                totalFields={progress?.totalFields}
                                remainingSeconds={progress?.remainingSeconds}
                            />
                        )}

                        {!!nextSectionEstimate?.approxMinutes && (
                            <Box
                                sx={{
                                    mt: 2,
                                    mb: 2,
                                    px: 2,
                                    py: 1.5,
                                    borderRadius: 2,
                                    textAlign: "center",
                                    backgroundColor: "rgba(25, 118, 210, 0.08)",
                                    border: "1px solid rgba(25, 118, 210, 0.2)"
                                }}
                            >
                                <strong>
                                    Your next section will take only {nextSectionEstimate.approxMinutes} min to complete
                                </strong>
                                {nextSectionEstimate.name ? `: ${nextSectionEstimate.name}` : ""}
                            </Box>
                        )}

                        {sectionCount > 5 ? (
                            <Box sx={formSectionsMoreThanFiveStyles.navWrap}>
                                <IconButton onClick={() => scrollStepper("left")} aria-label="Scroll steps left" size="small">
                                    <ChevronLeftIcon />
                                </IconButton>

                                <Box ref={stepperScrollRef} sx={formSectionsMoreThanFiveStyles.scrollBox}>
                                    <Stepper activeStep={displayStep} alternativeLabel sx={formSectionsMoreThanFiveStyles.stepper}>
                                        {visibleSectionIndices.map((fullIndex, displayIndex) => {
                                            const listItem = data.formSectionList[fullIndex];
                                            const stepProps: { completed?: boolean } = {};
                                            const sectionEnabled = isSectionEnabledWith(listItem, stepperFieldValues);
                                            stepProps.completed =
                                                sectionEnabled && ((listItem.id || 0) > 0 || fullIndex < (activeStep ?? 0));

                                            const nextIncomplete = getNextAllowedIncompleteFullIndex(
                                                data.formSectionList,
                                                stepWithLatestId,
                                                stepperFieldValues
                                            );
                                            const disabled =
                                                !sectionEnabled ||
                                                stepWithLatestId === null ||
                                                (!listItem.id && nextIncomplete !== fullIndex);

                                            return (
                                                <Step
                                                    key={fullIndex + "_" + listItem.id}
                                                    {...stepProps}
                                                    ref={(el) => {
                                                        stepRefs.current[displayIndex] = el;
                                                    }}
                                                >
                                                    <StepButton
                                                        disabled={disabled}
                                                        sx={!sectionEnabled ? { opacity: 0.4 } : {}}
                                                        onClick={() => {
                                                            if (sectionEnabled) setActiveStep(fullIndex);
                                                        }}
                                                    >
                                                        {renderSectionStepLabel(listItem)}
                                                    </StepButton>
                                                </Step>
                                            );
                                        })}
                                    </Stepper>
                                </Box>

                                <IconButton onClick={() => scrollStepper("right")} aria-label="Scroll steps right" size="small">
                                    <ChevronRightIcon />
                                </IconButton>
                            </Box>
                        ) : (
                            <Stepper activeStep={displayStep} alternativeLabel>
                                {visibleSectionIndices.map((fullIndex) => {
                                    const listItem = data.formSectionList[fullIndex];
                                    const stepProps: { completed?: boolean } = {};
                                    const sectionEnabled = isSectionEnabledWith(listItem, stepperFieldValues);
                                    stepProps.completed =
                                        sectionEnabled && ((listItem.id || 0) > 0 || fullIndex < (activeStep ?? 0));

                                    const nextIncomplete = getNextAllowedIncompleteFullIndex(
                                        data.formSectionList,
                                        stepWithLatestId,
                                        stepperFieldValues
                                    );
                                    const disabled =
                                        !sectionEnabled ||
                                        stepWithLatestId === null ||
                                        (!listItem.id && nextIncomplete !== fullIndex);

                                    return (
                                        <Step key={fullIndex + "_" + listItem.id} {...stepProps}>
                                            <StepButton
                                                disabled={disabled}
                                                sx={!sectionEnabled ? { opacity: 0.4 } : {}}
                                                onClick={() => {
                                                    if (sectionEnabled) setActiveStep(fullIndex);
                                                }}
                                            >
                                                {renderSectionStepLabel(listItem)}
                                            </StepButton>
                                        </Step>
                                    );
                                })}
                            </Stepper>
                        )}
                    </>
                )}

                {activeStep !== null && activeStep !== data?.formSectionList.length && (
                    <FormSectionOriginalManage
                        formId={effectiveFormId ?? 0}
                        formTemplateSectionId={data && data.formSectionList.length > 0 && activeStep < data.formSectionList.length ? data.formSectionList[activeStep].formTemplateSectionId : 0}
                        isSectionSaving={isSaving}
                        formSectionId={data?.id}
                        onSave={onSave}
                        isLastSection={isLastSection}
                    />
                )}
            </Container>
        </>
    );
}